import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  isDark: boolean;
  isAutoMode: boolean;
  setAutoMode: () => void;
  setManualTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage for saved theme preference
    const savedTheme = localStorage.getItem('mytaxifriend-theme') as Theme;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme;
    }
    
    // Default to system preference (auto mode)
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  });

  const [isAutoMode, setIsAutoMode] = useState<boolean>(() => {
    // Check if user has manually set a preference
    const savedTheme = localStorage.getItem('mytaxifriend-theme') as Theme;
    return !savedTheme; // Auto mode if no manual preference is set
  });

  // Get system preference
  const getSystemTheme = (): Theme => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    setIsAutoMode(false); // User manually changed theme
    localStorage.setItem('mytaxifriend-theme', newTheme);
  };

  const setAutoMode = () => {
    setIsAutoMode(true);
    localStorage.removeItem('mytaxifriend-theme'); // Remove manual preference
    // Set theme based on current system preference
    const systemTheme = getSystemTheme();
    setTheme(systemTheme);
  };

  const setManualTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    setIsAutoMode(false);
    localStorage.setItem('mytaxifriend-theme', newTheme);
  };

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    document.body.setAttribute('data-theme', theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't manually set a preference (auto mode)
      const savedTheme = localStorage.getItem('mytaxifriend-theme') as Theme;
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    // Add event listener for modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleSystemThemeChange);
    }
    
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }, []);

  // Update theme when auto mode changes
  useEffect(() => {
    if (isAutoMode) {
      const systemTheme = getSystemTheme();
      setTheme(systemTheme);
    }
  }, [isAutoMode]);

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    isDark: theme === 'dark',
    isAutoMode,
    setAutoMode,
    setManualTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 